import type { Id } from "@repo/convex/dataModel";

export interface HomeProject {
  _id: Id<"projects">;
  slug: string;
  name: string;
  owner?: string;
  description?: string;
  updatedAt?: number;
  folderId?: Id<"projectFolders">;
}

export interface HomeProjectFolder {
  folder: { _id: Id<"projectFolders">; name: string; parentFolderId?: Id<"projectFolders"> };
  projects: HomeProject[];
  children: HomeProjectFolder[];
}

export interface GroupedProjectsResult {
  folders: HomeProjectFolder[];
  unfiledProjects: HomeProject[];
}

/**
 * Flatten a nested folder tree into a simple list of { _id, name }.
 * Used by context menus to render the "Move to..." submenu.
 */
export function flattenFolders(
  folders: HomeProjectFolder[]
): { _id: Id<"projectFolders">; name: string }[] {
  const result: { _id: Id<"projectFolders">; name: string }[] = [];
  for (const node of folders) {
    result.push({ _id: node.folder._id, name: node.folder.name });
    result.push(...flattenFolders(node.children));
  }
  return result;
}

/**
 * Flatten the folder tree excluding a folder and all its descendants.
 * Used by folder context menus to show valid "Move to..." targets
 * (prevents circular nesting).
 */
export function getValidFolderMoveTargets(
  folders: HomeProjectFolder[],
  excludeFolderId: Id<"projectFolders">
): { _id: Id<"projectFolders">; name: string }[] {
  const result: { _id: Id<"projectFolders">; name: string }[] = [];
  for (const node of folders) {
    if (node.folder._id === excludeFolderId) {
      // Skip this folder and all its descendants
      continue;
    }
    result.push({ _id: node.folder._id, name: node.folder.name });
    result.push(...getValidFolderMoveTargets(node.children, excludeFolderId));
  }
  return result;
}
